import useFetcher from "@/utils/use-fetcher";
import {
  getListOrderSheetSetsKey,
  ListOrderSheetSetsRequest,
  ListOrderSheetSetsResponse,
} from "@/features/line-sheet-sets/hooks/use-list-order-sheet-sets";
import {
  getListLineSheetSetsKey,
  ListLineSheetSetsRequest,
  ListLineSheetSetsResponse,
} from "@/features/line-sheet-sets/hooks/use-list-line-sheet-sets";
import { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import {
  ListDenseOrderSheetSetsRequest,
  ListDenseOrderSheetSetsResponse,
  parseListDenseOrderSheetSetsKey,
} from "@/features/order-sheet-sets/hooks/use-list-dense-order-sheet-set";
import { uniq } from "lodash";
import { DenseOrderSheet } from "@/features/order-sheets/order-sheet.type";
import { OrderByItem } from "@/features/types";
import {
  MISSING_TEXT,
  placeholderLineSheet,
  placeholderLineSheetSet,
} from "@/features/order-sheet-sets/helpers/missing-line-sheet-set";
import { reportError } from "@/utils/sentry-report";

export default function useListDenseOrderSheetSetsFetcher() {
  const listOrderSheetSetsFetcher = useFetcher<
    ListOrderSheetSetsRequest,
    ListOrderSheetSetsResponse
  >();

  const listLineSheetSetsFetcher = useFetcher<
    ListLineSheetSetsRequest,
    ListLineSheetSetsResponse
  >();

  return useMemo(() => {
    return async (
      config: AxiosRequestConfig<ListDenseOrderSheetSetsRequest>
    ): Promise<ListDenseOrderSheetSetsResponse> => {
      const { parameter, request } = parseListDenseOrderSheetSetsKey(config);
      const listOrderSheetSetsResponse = await listOrderSheetSetsFetcher(
        getListOrderSheetSetsKey(parameter, request)!
      );

      if (listOrderSheetSetsResponse.orderSheetSets.length > 0) {
        const lineSheetSetIds = uniq(
          listOrderSheetSetsResponse.orderSheetSets.map(
            (i) => i.lineSheetSet.id
          )
        );

        const listLineSheetSetsResponse = await listLineSheetSetsFetcher(
          getListLineSheetSetsKey(
            {
              by: parameter.by,
              companyId: parameter.companyId,
            },
            {
              id__in: lineSheetSetIds,
              pageNumber: 1,
              pageSize: lineSheetSetIds.length,
              orderBy: [new OrderByItem("createdAt", "descend")],
            }
          )!
        );

        // 볼 수 없는 라인시트 세트(발행 해제·공유 해제)에 걸린 오더 — 자리표시로 그리고 Sentry 로 알린다.
        const missingOrderSheetSetIds: number[] = [];

        const result = Promise.resolve({
          totalCount: listOrderSheetSetsResponse.totalCount,
          statistics: listOrderSheetSetsResponse.statistics,
          orderSheetSets: listOrderSheetSetsResponse.orderSheetSets.map(
            (orderSheetSet) => {
              const foundLineSheetSet =
                listLineSheetSetsResponse.lineSheetSets.find(
                  (j) => j.id === orderSheetSet.lineSheetSet.id
                );
              if (!foundLineSheetSet) {
                missingOrderSheetSetIds.push(orderSheetSet.id);
              }
              const lineSheetSet =
                foundLineSheetSet ??
                placeholderLineSheetSet(
                  orderSheetSet.lineSheetSet.id,
                  orderSheetSet.toBeSubmitted
                );
              const { orderSheets: _orderSheets, ...rest } = orderSheetSet;

              const orderSheets = _orderSheets.map(
                (orderSheet): DenseOrderSheet => {
                  const { lineSheet: _lineSheet, ...rest } = orderSheet;

                  const lineSheet =
                    lineSheetSet.lineSheets.find((lineSheet) => {
                      return lineSheet.id === orderSheet.lineSheet.id;
                    }) ?? placeholderLineSheet(orderSheet.lineSheet.id);

                  const orderSheetTabs = orderSheet.orderSheetTabs.map(
                    (orderSheetTab) => {
                      const lineSheetTab = lineSheet.lineSheetTabs.find(
                        (i) => i.index === orderSheetTab.index
                      );
                      return {
                        ...orderSheetTab,
                        name: lineSheetTab?.name ?? MISSING_TEXT,
                      };
                    }
                  );

                  return {
                    ...rest,
                    orderSheetTabs,
                    lineSheet: lineSheet,
                  };
                }
              );

              return {
                ...rest,
                orderSheets: orderSheets,
                lineSheetSet: lineSheetSet,
              };
            }
          ),
        });

        if (missingOrderSheetSetIds.length > 0) {
          // 메시지를 고정해 한 이슈로 묶는다 — 어떤 오더인지는 extra 로 남긴다.
          reportError(
            new Error("오더 목록: 볼 수 없는 라인시트 세트에 걸린 오더"),
            {
              source: "dense-order-sheet-sets",
              by: parameter.by,
              companyId: parameter.companyId,
              orderSheetSetIds: missingOrderSheetSetIds,
            }
          );
        }
        return result;
      } else {
        return Promise.resolve({
          totalCount: 0,
          statistics: listOrderSheetSetsResponse.statistics,
          orderSheetSets: [],
        });
      }
    };
  }, [listOrderSheetSetsFetcher, listLineSheetSetsFetcher]);
}
